import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  formatMoney,
  msUntilExpiry,
  effectiveIntentStatus,
  humanizeApiError,
  humanizeIntentError,
  type ShoppingSummary,
  type ShoppingCartSnapshot,
  type LapoOrderIntent,
  type ClaveroHealth,
} from '@lapo/core';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * CREEL — the right-drawer Shopping panel (replaces the legacy Amazon-Business OrdersView slot). A
 * glanceable view of the Clavero-backed Amazon session: health + spend strip, pending purchase
 * intents (with a live TTL countdown + Confirm/Cancel), the cached cart (qty stepper, remove,
 * clear-with-stash), the stash (Restore = a NEW priced purchase intent, D7), and the head of the
 * synced order history. Depth (search, full history, audit) lives on the `#/shopping` page.
 *
 * Presentational-with-fetch: the component self-fetches the summary through the injected api subset,
 * so the drawer stays fast and independent of the app store. Reuses the shipping/orders `lapo-ship-*`
 * drawer styles where practical, with `lapo-shop-*` additions for the shopping-specific chrome.
 */
export interface ShoppingDrawerApi {
  getShopping(): Promise<ShoppingSummary>;
  refreshShopping(): Promise<ShoppingSummary>;
  /** Poll one intent — the server refreshes it from Clavero and finalizes a BUILDING→PENDING transition. */
  getShoppingIntent(id: string): Promise<LapoOrderIntent>;
  confirmShoppingIntent(id: string, surface: 'web' | 'mobile'): Promise<LapoOrderIntent>;
  cancelShoppingIntent(id: string): Promise<LapoOrderIntent>;
  setCartQty(asin: string, qty: number): Promise<ShoppingCartSnapshot>;
  removeCartLine(asin: string): Promise<ShoppingCartSnapshot>;
  clearCart(): Promise<ShoppingCartSnapshot>;
  restoreStash(): Promise<unknown>;
  /** Discard the stash without re-ordering (the ✕ on the "Stashed cart" card). */
  discardStash(): Promise<unknown>;
  /** Place the current cart as a priced order (stashes+clears the cart, then drafts). Small in-rules
   * orders auto-place per the auto-confirm dial; larger ones land as a PENDING order to confirm. */
  placeCartOrder(): Promise<unknown>;
}

export interface ShoppingDrawerProps {
  api: ShoppingDrawerApi;
  /** Open the full `#/shopping` page ("View all"). */
  onOpenShopping?: () => void;
  /** Shown instead of the panel when shopping is unavailable (no API / signed out). */
  disabledReason?: string;
}

/** A pill-shaped status chip (Chrome / Signed in / HALT / Needs you) with a colored dot. */
function StatusPill({ on, label, tone = 'good' }: { on: boolean; label: string; tone?: 'good' | 'warn' | 'bad' }): ReactElement {
  return (
    <span className={`lapo-shop-pill is-${on ? tone : 'off'}`} aria-label={`${label}: ${on ? 'yes' : 'no'}`}>
      <span className="lapo-shop-pill-dot" aria-hidden />
      {label}
    </span>
  );
}

/** A rounded thumbnail tile standing in for a product image (the cart wire has no image URL). */
function Thumb({ icon = 'shopping_bag' }: { icon?: string }): ReactElement {
  return (
    <span className="lapo-shop-thumb" aria-hidden>
      <MaterialIcon name={icon} size={18} />
    </span>
  );
}

/** A section header: an accent-tinted icon, a title, an optional count chip, and an optional right slot. */
function SectionHead({ icon, title, count, right }: { icon: string; title: string; count?: number; right?: ReactElement | null }): ReactElement {
  return (
    <div className="lapo-shop-section-head">
      <h3 className="lapo-shop-h3">
        <MaterialIcon name={icon} size={16} className="lapo-shop-h3-icon" />
        {title}
        {typeof count === 'number' ? <span className="lapo-shop-count">{count}</span> : null}
      </h3>
      {right ?? null}
    </div>
  );
}

/** "0:59" style mm:ss from a millisecond span (clamped at 0). */
function mmss(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function HealthStrip({
  health,
  lastSyncAt,
  lastError,
  onRefresh,
  refreshing,
}: {
  health?: ClaveroHealth;
  lastSyncAt?: number;
  lastError?: string;
  onRefresh: () => void;
  refreshing: boolean;
}): ReactElement {
  const spend = health?.spend;
  return (
    <div className="lapo-shop-strip">
      <div className="lapo-shop-strip-top">
        <span className="lapo-shop-strip-glyph" aria-hidden>
          <MaterialIcon name="storefront" size={18} />
        </span>
        <div className="lapo-shop-pills">
          <StatusPill on={!!health?.chromeReady} label="Chrome" />
          <StatusPill on={!!health?.signedIn} label="Signed in" />
          {health?.halted ? <StatusPill on label="Halted" tone="bad" /> : null}
          {health?.needsHuman ? <StatusPill on label="Needs you" tone="warn" /> : null}
        </div>
      </div>
      {spend ? (
        <div className="lapo-shop-spend-row">
          <div className="lapo-shop-spend-cell">
            <span className="lapo-shop-spend-k">Today</span>
            <span className="lapo-shop-spend-v">{formatMoney(spend.todayCents)}</span>
          </div>
          <div className="lapo-shop-spend-cell">
            <span className="lapo-shop-spend-k">This week</span>
            <span className="lapo-shop-spend-v">{formatMoney(spend.weekCents)}</span>
          </div>
        </div>
      ) : null}
      <div className="lapo-shop-strip-foot">
        <span className="lapo-shop-stale">
          {lastSyncAt ? `Updated ${new Date(lastSyncAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Not yet synced'}
        </span>
        <button type="button" className="lapo-set-btn lapo-shop-refresh-btn" onClick={onRefresh} disabled={refreshing}>
          <MaterialIcon name="refresh" size={15} className={refreshing ? 'lapo-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {lastError ? <div className="lapo-shop-error">{lastError}</div> : null}
    </div>
  );
}

function IntentCard({
  intent,
  now,
  onConfirm,
  onCancel,
  busy,
}: {
  intent: LapoOrderIntent;
  now: number;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}): ReactElement {
  const left = msUntilExpiry(intent, now);
  const expired = left <= 0;
  return (
    <div className="lapo-shop-intent">
      <div className="lapo-shop-intent-head">
        <span className="lapo-shop-intent-total">{formatMoney(intent.grandTotalCents)}</span>
        <span className={`lapo-shop-intent-ttl${expired ? ' is-expired' : ''}`}>{expired ? 'Expired' : mmss(left)}</span>
      </div>
      <ul className="lapo-shop-intent-lines">
        {intent.items.map((l) => (
          <li key={l.asin}>
            <span className="lapo-shop-line-qty">{l.qty}×</span> {l.title}
            <span className="lapo-shop-line-price">{formatMoney(l.lineTotalCents)}</span>
            {/* A sellerRating is only present for third-party sellers (Clavero omits it on Amazon-sold
                listings), so surface the seller + feedback so the owner can judge a 3P before confirming. */}
            {l.sellerRating ? (
              <span className="lapo-shop-line-seller" title={`Sold by ${l.seller ?? 'third-party seller'}`}>
                {l.seller ? `${l.seller} · ` : ''}{l.sellerRating}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="lapo-shop-intent-meta">
        Ship to {intent.shipTo} · {intent.payment}
        {intent.autoEligible ? ' · auto-eligible' : ''}
      </div>
      {intent.guardrails && intent.guardrails.length > 0 ? (
        <div className="lapo-shop-intent-guards">{intent.guardrails.join(' · ')}</div>
      ) : null}
      <div className="lapo-shop-intent-actions">
        <button type="button" className="lapo-set-btn is-primary lapo-shop-confirm" onClick={onConfirm} disabled={busy || expired}>
          <MaterialIcon name="lock" size={15} />
          Confirm order
        </button>
        <button type="button" className="lapo-set-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** An intent Clavero is still building (async pricing, ~2-6 min). No total/countdown yet; Cancel allowed. */
function BuildingIntentCard({ onCancel, busy }: { onCancel: () => void; busy: boolean }): ReactElement {
  return (
    <div className="lapo-shop-intent is-building">
      <div className="lapo-shop-intent-building">
        <MaterialIcon name="progress_activity" size={16} className="lapo-spin" />
        <span>Preparing your order…</span>
      </div>
      <div className="lapo-shop-intent-actions">
        <button type="button" className="lapo-set-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ShoppingDrawer({ api, onOpenShopping, disabledReason }: ShoppingDrawerProps): ReactElement {
  const [summary, setSummary] = useState<ShoppingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // A build that failed (guardrail / stock / timeout) so the "Preparing…" card doesn't just vanish.
  const [buildError, setBuildError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await api.getShopping());
    } catch (err) {
      setError(humanizeApiError(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // Tick once a second while there are pending intents, to drive the TTL countdowns.
  const hasPending = (summary?.pendingIntents?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasPending]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      setSummary(await api.refreshShopping());
    } catch (err) {
      setError(humanizeApiError(err));
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  const guard = useCallback(
    async (id: string, fn: () => Promise<unknown>) => {
      setBusyId(id);
      setError(null);
      try {
        await fn();
        await load();
      } catch (err) {
        setError(humanizeApiError(err));
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const pending = useMemo(
    () => (summary?.pendingIntents ?? []).filter((i) => effectiveIntentStatus(i, now) === 'PENDING'),
    [summary, now],
  );
  const building = useMemo(() => (summary?.pendingIntents ?? []).filter((i) => i.status === 'BUILDING'), [summary]);

  // While Clavero is still building an intent, poll each one every ~5s. Hitting getShoppingIntent drives
  // the server to refresh from Clavero and finalize BUILDING→PENDING; on any transition we reload the
  // whole summary (to pick up an auto-confirm / cart / spend change), otherwise merge the fresh mirror in.
  const buildingKey = useMemo(() => building.map((i) => i.intentId).join('|'), [building]);
  useEffect(() => {
    if (!buildingKey) return;
    setBuildError(null); // a new build is in flight — clear any stale failure notice
    const ids = buildingKey.split('|');
    let cancelled = false;
    const t = setInterval(() => {
      void (async () => {
        const results = await Promise.all(ids.map((id) => api.getShoppingIntent(id).catch(() => null)));
        if (cancelled) return;
        const fresh = results.filter((r): r is LapoOrderIntent => r != null);
        if (fresh.some((r) => r.status !== 'BUILDING')) {
          // Don't let a failed build silently disappear when load() drops it from the pending list.
          const failed = fresh.find((r) => r.status === 'FAILED' || r.status === 'NEEDS_HUMAN' || r.status === 'ABORTED_DRIFT');
          if (failed) setBuildError(humanizeIntentError(failed.error));
          void load();
          return;
        }
        if (fresh.length > 0) {
          const byId = new Map(fresh.map((r) => [r.intentId, r]));
          setSummary((prev) => (prev ? { ...prev, pendingIntents: prev.pendingIntents.map((i) => byId.get(i.intentId) ?? i) } : prev));
        }
      })();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [buildingKey, api, load]);

  const cart = summary?.cart;
  const stash = summary?.stash;
  const recent = (summary?.recentOrders ?? []).slice(0, 5);

  if (disabledReason) {
    return <div className="lapo-shop-empty">{disabledReason}</div>;
  }
  if (loading && !summary) {
    return (
      <div className="lapo-shop-empty">
        <MaterialIcon name="progress_activity" size={22} className="lapo-spin" />
        <span>Loading…</span>
      </div>
    );
  }
  if (summary && !summary.connected) {
    return (
      <div className="lapo-shop-empty">
        <span className="lapo-shop-empty-glyph"><MaterialIcon name="shopping_bag" size={26} /></span>
        <p className="lapo-shop-empty-title">Amazon shopping isn’t connected.</p>
        <p className="lapo-shop-empty-hint">{summary.disabledReason ?? 'Add your Clavero endpoint and tokens in Settings → Integrations → Amazon.'}</p>
      </div>
    );
  }

  return (
    <div className="lapo-shop-drawer">
      {error ? <div className="lapo-shop-error">{error}</div> : null}
      {buildError ? (
        <div className="lapo-shop-error" role="alert">
          <MaterialIcon name="error" size={16} />
          <span>{buildError}</span>
          <button type="button" className="lapo-shop-error-dismiss" onClick={() => setBuildError(null)} aria-label="Dismiss">
            <MaterialIcon name="close" size={14} />
          </button>
        </div>
      ) : null}

      <HealthStrip
        health={summary?.health}
        lastSyncAt={summary?.lastSyncAt}
        lastError={summary?.lastError}
        onRefresh={() => void onRefresh()}
        refreshing={refreshing}
      />

      {pending.length + building.length > 0 ? (
        <section className="lapo-shop-section">
          <SectionHead icon="pending_actions" title={`Pending ${pending.length + building.length === 1 ? 'order' : 'orders'}`} count={pending.length + building.length} />
          {building.map((intent) => (
            <BuildingIntentCard
              key={intent.intentId}
              busy={busyId === intent.intentId}
              onCancel={() => void guard(intent.intentId, () => api.cancelShoppingIntent(intent.intentId))}
            />
          ))}
          {pending.map((intent) => (
            <IntentCard
              key={intent.intentId}
              intent={intent}
              now={now}
              busy={busyId === intent.intentId}
              onConfirm={() => void guard(intent.intentId, () => api.confirmShoppingIntent(intent.intentId, 'web'))}
              onCancel={() => void guard(intent.intentId, () => api.cancelShoppingIntent(intent.intentId))}
            />
          ))}
        </section>
      ) : null}

      <section className="lapo-shop-section">
        <SectionHead
          icon="shopping_cart"
          title="Cart"
          count={cart?.itemCount}
          right={
            <a className="lapo-shop-link" href="https://www.amazon.com/gp/cart/view.html" target="_blank" rel="noreferrer noopener">
              amazon.com <MaterialIcon name="open_in_new" size={13} />
            </a>
          }
        />
        {!cart || cart.lines.length === 0 ? (
          <div className="lapo-shop-empty-mini">
            <MaterialIcon name="remove_shopping_cart" size={20} />
            <span>Your cart is empty.</span>
          </div>
        ) : (
          <>
            <ul className="lapo-shop-cart">
              {cart.lines.map((l) => (
                <li key={l.asin} className="lapo-shop-cart-line">
                  <Thumb />
                  <div className="lapo-shop-cart-main">
                    <span className="lapo-shop-cart-title">{l.title}</span>
                    {typeof l.priceCents === 'number' ? <span className="lapo-shop-cart-price">{formatMoney(l.priceCents)}</span> : null}
                  </div>
                  <span className="lapo-shop-qty">
                    <button
                      type="button"
                      className="lapo-shop-step"
                      aria-label={`Decrease quantity of ${l.title}`}
                      disabled={busyId === l.asin}
                      onClick={() => void guard(l.asin, () => (l.qty <= 1 ? api.removeCartLine(l.asin) : api.setCartQty(l.asin, l.qty - 1)))}
                    >
                      −
                    </button>
                    <span className="lapo-shop-qty-n">{l.qty}</span>
                    <button
                      type="button"
                      className="lapo-shop-step"
                      aria-label={`Increase quantity of ${l.title}`}
                      disabled={busyId === l.asin}
                      onClick={() => void guard(l.asin, () => api.setCartQty(l.asin, l.qty + 1))}
                    >
                      +
                    </button>
                  </span>
                  <button
                    type="button"
                    className="lapo-shop-remove"
                    aria-label={`Remove ${l.title}`}
                    disabled={busyId === l.asin}
                    onClick={() => void guard(l.asin, () => api.removeCartLine(l.asin))}
                  >
                    <MaterialIcon name="close" size={15} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="lapo-shop-cart-actions">
              <button
                type="button"
                className="lapo-set-btn is-primary lapo-shop-place"
                disabled={busyId === '__place__'}
                onClick={() => void guard('__place__', () => api.placeCartOrder())}
              >
                <MaterialIcon name="shopping_cart_checkout" size={16} />
                {busyId === '__place__' ? 'Placing…' : 'Place order'}
              </button>
              <button
                type="button"
                className="lapo-set-btn lapo-shop-clear"
                disabled={busyId === '__clear__'}
                onClick={() => void guard('__clear__', () => api.clearCart())}
              >
                Clear cart (stash for later)
              </button>
            </div>
            <p className="lapo-shop-hint">
              Placing prices your cart as an order. Small orders inside your auto-confirm rules place
              automatically; anything larger waits here for you to confirm.
            </p>
          </>
        )}
      </section>

      {stash ? (
        <section className="lapo-shop-section lapo-shop-stash">
          <SectionHead
            icon="inventory_2"
            title="Stashed cart"
            right={
              <button
                type="button"
                className="lapo-shop-remove"
                aria-label="Discard stashed cart"
                title="Discard — throw this stash away without re-ordering"
                disabled={busyId === '__discardstash__'}
                onClick={() => void guard('__discardstash__', () => api.discardStash())}
              >
                <MaterialIcon name="close" size={16} />
              </button>
            }
          />
          <p className="lapo-shop-muted">Stashed {new Date(stash.stashedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.</p>
          <ul className="lapo-shop-cart">
            {stash.lines.map((l) => (
              <li key={l.asin} className="lapo-shop-cart-line">
                <Thumb icon="inventory_2" />
                <div className="lapo-shop-cart-main">
                  <span className="lapo-shop-cart-title">
                    <span className="lapo-shop-line-qty">{l.qty}×</span> {l.title}
                  </span>
                  {typeof l.priceCents === 'number' ? <span className="lapo-shop-cart-price">{formatMoney(l.priceCents)}</span> : null}
                </div>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="lapo-set-btn is-primary lapo-shop-restore"
            disabled={busyId === '__restore__'}
            onClick={() => void guard('__restore__', () => api.restoreStash())}
          >
            <MaterialIcon name="restart_alt" size={16} />
            Restore as a priced order
          </button>
          <p className="lapo-shop-hint">Restoring drafts a new priced purchase intent — confirming it places the order.</p>
        </section>
      ) : null}

      <section className="lapo-shop-section">
        <SectionHead
          icon="receipt_long"
          title="Recent orders"
          right={
            onOpenShopping ? (
              <button type="button" className="lapo-shop-link" onClick={onOpenShopping}>
                View all
              </button>
            ) : null
          }
        />
        {recent.length === 0 ? (
          <div className="lapo-shop-empty-mini">
            <MaterialIcon name="package_2" size={20} />
            <span>No synced orders yet.</span>
          </div>
        ) : (
          <ul className="lapo-shop-orders">
            {recent.map((o) => (
              <li key={o.orderId} className="lapo-shop-order">
                <span className="lapo-shop-order-date">{new Date(o.placedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                <span className="lapo-shop-order-desc">
                  {o.lines.length > 0 ? o.lines[0]!.title : o.orderId}
                  {o.lines.length > 1 ? <span className="lapo-shop-order-more"> +{o.lines.length - 1}</span> : null}
                </span>
                <span className="lapo-shop-order-total">{formatMoney(o.grandTotalCents)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
